/**
 * Pand CTA — scroll-triggered guide popup (frontend behaviour).
 *
 * Slides in once scrolled past the trigger; hides above it, at the footer, or
 * while the CookieYes banner is up. Closing hides it for the current page view
 * only — it returns on reload or when navigating to another page.
 *
 * @package Interact_26
 */

export {};

function onReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

function initPandCta(el: HTMLElement): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.classList.add('no-anim');
  }

  const scrollShowPx = Math.max(0, Number(el.dataset.scrollShowPx ?? 200));

  let footerInView = false;
  let consentBlocking = false;
  let dismissed = false;
  let visible = false;

  // The CookieYes "revisit consent" button shares this corner — hide it while
  // the popup is on screen, restore it otherwise. Resolved lazily (renders async).
  let revisitEl: HTMLElement | null = null;
  const setRevisitVisible = (revisitVisible: boolean): void => {
    if (!revisitEl || !document.body.contains(revisitEl)) {
      revisitEl = document.querySelector<HTMLElement>('.cky-btn-revisit-wrapper');
    }
    if (!revisitEl) return;
    revisitEl.style.transition = 'opacity 0.2s ease';
    revisitEl.style.opacity = revisitVisible ? '' : '0';
    revisitEl.style.visibility = revisitVisible ? '' : 'hidden';
    revisitEl.style.pointerEvents = revisitVisible ? '' : 'none';
  };

  // Toggle visibility, focusability (inert) and a11y exposure together.
  const applyVisible = (next: boolean): void => {
    el.classList.toggle('is-visible', next);
    el.inert = !next;
    el.setAttribute('aria-hidden', String(!next));
    setRevisitVisible(!next);
  };

  const show = (): void => {
    if (dismissed || visible) return;
    visible = true;
    applyVisible(true);
  };

  const hide = (): void => {
    if (!visible) return;
    visible = false;
    applyVisible(false);
  };

  // Re-evaluated on every scroll: visible only past the trigger, above the
  // footer, with no cookie banner covering it.
  const update = (): void => {
    if (dismissed) return;
    if (!consentBlocking && window.scrollY > scrollShowPx && !footerInView) {
      show();
    } else {
      hide();
    }
  };
  window.addEventListener('scroll', update, { passive: true });

  const footer = document.querySelector('#colophon, .site-footer');
  let footerObserver: IntersectionObserver | null = null;
  if (footer && 'IntersectionObserver' in window) {
    footerObserver = new IntersectionObserver(
      (entries) => {
        footerInView = entries.some((entry) => entry.isIntersecting);
        update();
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    footerObserver.observe(footer);
  }

  // Suppress the popup only while the CookieYes banner is actually on screen.
  const bannerIsBlocking = (banner: Element | null): boolean =>
    !!banner && !banner.classList.contains('cky-hide');

  let bannerClassObserver: MutationObserver | null = null;
  let bannerAppearObserver: MutationObserver | null = null;

  const watchBannerClass = (banner: Element): void => {
    bannerClassObserver = new MutationObserver(() => {
      consentBlocking = bannerIsBlocking(banner);
      update();
    });
    bannerClassObserver.observe(banner, { attributes: true, attributeFilter: ['class'] });
  };

  const existingBanner = document.querySelector('.cky-consent-container');
  consentBlocking = bannerIsBlocking(existingBanner);
  if (existingBanner) {
    watchBannerClass(existingBanner);
  } else {
    // Banner may load async — watch for it to appear, then track its state.
    bannerAppearObserver = new MutationObserver(() => {
      const banner = document.querySelector('.cky-consent-container');
      if (!banner) return;
      bannerAppearObserver?.disconnect();
      bannerAppearObserver = null;
      consentBlocking = bannerIsBlocking(banner);
      update();
      watchBannerClass(banner);
    });
    bannerAppearObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      bannerAppearObserver?.disconnect();
      bannerAppearObserver = null;
    }, 10000);
  }

  const close = (): void => {
    dismissed = true;
    hide();
    window.removeEventListener('scroll', update);
    footerObserver?.disconnect();
    bannerClassObserver?.disconnect();
    bannerAppearObserver?.disconnect();
    el.hidden = true;
  };

  el.querySelector<HTMLElement>('[data-pand-close]')?.addEventListener('click', (e) => {
    e.preventDefault();
    close();
  });

  // Reveal into layout (off-screen) and set the initial hidden/inert state.
  el.hidden = false;
  applyVisible(false);
  update();
}

function bootstrap(): void {
  const el = document.getElementById('pand-cta');
  if (el) initPandCta(el);
}

onReady(bootstrap);
